/**
 * The detail panel's "member" mode. DUMB atomic SchmancyElement: reads the
 * memberView computed and writes intents via members.state's membersAction$
 * channel (members.sync.ts, web-side, performs the write).
 *
 * assignGrant/removeGrant patch the member's own grant(s) directly — one
 * doc for an all-events member, one per event for an event-scoped one
 * (members.api.ts fans the callable out over the member's own `scope`,
 * carried on the dispatched intent); a server trigger keeps the member doc
 * in sync.
 *
 * Guardrails (`memberView.canChangeRole` / `canRemove` / `*LockReason`): an
 * actor can't change their own role or remove themselves, and nobody can
 * demote or remove the org's sole remaining Owner — both controls stay
 * disabled with a visible reason rather than silently inert.
 *
 * <permission-picker readonly> below answers "what does this member hold
 * right now" (ongoing visibility) — a different job from <select-role>'s
 * per-chip overlay, which previews what a CANDIDATE role would grant before
 * switching.
 */

import { awaitByRequestId$ } from "@mhmo91/rx";
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { confirm } from "@mhmo91/schmancy/overlay";
import { html, type TemplateResult } from "lit";
import { state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { firstValueFrom, takeUntil } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import {
	detailState,
	membersAction$,
	membersResult$,
	memberView,
	rolesState,
} from "../members.state.ts";
import "../roles/permission-picker.view.ts";
import "../roles/select-role.view.ts";

@schmancyElement("member-detail")
export class MemberDetail extends LocalizeMixin(SchmancyElement) {
	@state() private pending: "role" | "remove" | null = null;
	@state() private pendingRoleId = "";
	@state() private failedRoleId = "";
	@state() private removeFailed = false;

	private async changeRole(uid: string, roleId: string): Promise<void> {
		if (this.pending) return;
		const current = memberView.get();
		const role = rolesState.value.find((candidate) => candidate.id === roleId);
		if (
			!current ||
			current.uid !== uid ||
			!current.canChangeRole ||
			!role ||
			current.roleId === roleId
		)
			return;
		this.pending = "role";
		this.pendingRoleId = roleId;
		try {
			if (
				!(await confirm({
					title: this.t("admin.members.detail.changeRoleConfirmTitle", {
						roleName: role.name,
					}),
					message: this.t("admin.members.detail.changeRoleConfirmMessage"),
					confirmText: this.t("admin.members.detail.changeRoleConfirmText"),
					cancelText: this.t("admin.common.cancel"),
				}))
			)
				return;
			const atConfirm = memberView.get();
			if (!atConfirm || atConfirm.uid !== uid || !atConfirm.canChangeRole) {
				this.failedRoleId = roleId;
				return;
			}
			const requestId = crypto.randomUUID();
			const answer = firstValueFrom(
				awaitByRequestId$(membersResult$, requestId).pipe(
					takeUntil(this.disconnecting),
				),
			);
			membersAction$.next({
				kind: "assignGrant",
				requestId,
				targetUid: uid,
				roleId,
				scope: atConfirm.scope,
			});
			if (!(await answer).ok) this.failedRoleId = roleId;
			else this.failedRoleId = "";
		} catch {
			this.failedRoleId = roleId;
		} finally {
			this.pending = null;
			this.pendingRoleId = "";
		}
	}

	private async removeMember(uid: string): Promise<void> {
		if (this.pending) return;
		const current = memberView.get();
		if (!current || current.uid !== uid || !current.canRemove) return;
		this.pending = "remove";
		try {
			if (
				!(await confirm({
					title: this.t("admin.members.detail.removeConfirmTitle"),
					message: this.t("admin.members.detail.removeConfirmMessage"),
					confirmText: this.t("admin.members.detail.removeConfirmText"),
					cancelText: this.t("admin.common.cancel"),
					variant: "danger",
				}))
			)
				return;
			const atConfirm = memberView.get();
			if (!atConfirm || atConfirm.uid !== uid || !atConfirm.canRemove) {
				this.removeFailed = true;
				return;
			}
			const requestId = crypto.randomUUID();
			const answer = firstValueFrom(
				awaitByRequestId$(membersResult$, requestId).pipe(
					takeUntil(this.disconnecting),
				),
			);
			membersAction$.next({
				kind: "removeGrant",
				requestId,
				targetUid: uid,
				scope: atConfirm.scope,
			});
			if (!(await answer).ok) this.removeFailed = true;
			else detailState.replace({ kind: "none" });
		} catch {
			this.removeFailed = true;
		} finally {
			this.pending = null;
		}
	}
	override render(): TemplateResult {
		const v = memberView.get();
		if (!v) return html``;
		const {
			uid,
			displayName,
			email,
			roleId,
			grants,
			initials,
			scopeLabel,
			canManageMembers,
			canChangeRole,
			canRemove,
			roleLockReason,
			removeLockReason,
			grantedByLabel,
			grantedAtLabel,
		} = v;

		return html`
			<div class="flex flex-col h-full">
				<div class="flex items-center gap-3 px-5 pt-5 pb-3">
					<schmancy-avatar class="w-11 h-11 shrink-0" aria-hidden="true" .initials=${initials}></schmancy-avatar>
					<div class="min-w-0 flex-1">
						<schmancy-typography type="title" token="md" class="truncate">${displayName}</schmancy-typography>
						<schmancy-typography type="body" token="sm" class="text-surface-onVariant block truncate">${email}</schmancy-typography>
					</div>
					<schmancy-icon-button
						icon="close"
						?disabled=${this.pending !== null}
						variant="text"
						aria-label=${this.t("admin.members.detail.closeAriaLabel")}
						@click=${() => detailState.replace({ kind: "none" })}
					></schmancy-icon-button>
				</div>

				<div class="px-5 py-2 flex-1 min-h-0 flex flex-col gap-4">
					<div class="flex flex-col gap-2 shrink-0">
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.members.common.roleLabel")}</schmancy-typography>
						<select-role
							.value=${roleId}
							?disabled=${!canChangeRole}
							.pendingRoleId=${this.pendingRoleId}
							.failedRoleId=${this.failedRoleId}
							@change=${(event: CustomEvent<{ value: string }>) => this.changeRole(uid, event.detail.value)}
						></select-role>
						${when(
							roleLockReason,
							() => html`
								<schmancy-typography type="body" token="xs" class="text-surface-onVariant">${roleLockReason}</schmancy-typography>
							`,
						)}
					</div>

					<div class="flex flex-col gap-1 shrink-0">
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.members.detail.scopeLabel")}</schmancy-typography>
						<schmancy-typography type="body" token="md">${scopeLabel}</schmancy-typography>
					</div>

					${when(
						grantedByLabel,
						() => html`
							<div class="flex flex-col gap-1 shrink-0">
								<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.members.detail.grantedLabel")}</schmancy-typography>
								<schmancy-typography type="body" token="md">${this.t("admin.members.detail.grantedByOn", { name: grantedByLabel ?? "", date: grantedAtLabel })}</schmancy-typography>
							</div>
						`,
					)}

					<div class="flex flex-col gap-2 flex-1 min-h-0">
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant shrink-0"> ${this.t("admin.members.common.permissionsLabel")} </schmancy-typography>
						<div ${overflowWithin()} class="flex-1 min-h-0 flex flex-col gap-2 pb-4">
							<permission-picker readonly .selected=${new Set(grants)}></permission-picker>
						</div>
					</div>
				</div>

				<!-- Hidden when actor lacks tickets.members.invite -->
				${when(
					canManageMembers,
					() => html`
						<schmancy-surface opacity="translucent" elevated class="sticky bottom-0 px-5 py-4 pt-2 flex flex-col gap-2">
							<schmancy-button
								variant="outlined"
								color="error"
								class="w-full"
								?disabled=${!canRemove || this.pending === "role"}
								.busy=${this.pending === "remove"}
								@click=${() => this.removeMember(uid)}
							>
								<schmancy-icon slot="prefix" aria-hidden="true">person_remove</schmancy-icon>
								${this.t(this.removeFailed ? "admin.common.retry" : "admin.members.detail.removeButton")}
							</schmancy-button>
							${when(
								removeLockReason,
								() => html`
									<schmancy-typography type="body" token="xs" class="text-surface-onVariant">${removeLockReason}</schmancy-typography>
								`,
							)}
						</schmancy-surface>
					`,
				)}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"member-detail": MemberDetail;
	}
}
